/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { isEmpty, find, filter } from 'lodash';
import cn from 'classnames';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { useTranslation } from '../../src/i18n';
import { route } from '../../constants/routes';
import { getRouteWithSlug } from '../../utils/common';
import { TP, EMPTY_INFO_MARK } from '../../constants/consts';
import OuterLink from '../OuterLink';

// dynamic imports
const Grid = dynamic(() => import('@material-ui/core/Grid'));
const NextLink = dynamic(() => import('../NextLink'));
const CastRow = dynamic(() => import('./castRow'));
const TitledBlock = dynamic(() => import('../TitledBlock'));

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(2)} 0`,
    flexDirection: 'row',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      justifyContent: 'flex-start',
    },
  },
  types: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  teamItem: {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 200,
  },
  castItem: {
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      maxWidth: '100%',
      marginBottom: 20,
    },
  },
  otherItem: {
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      maxWidth: '100%',
    },
  },
  teamItemBlock: {
    width: '100%',
  },
  tourRow: {
    display: 'flex',
    padding: '5px 0',
    margin: '-4px 0',
  },
  tourDate: {
    marginRight: 8,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    padding: '4px 0',
  },
  cellBody: {
    lineHeight: 1.2,
    padding: '4px 0',
  },
}));

const ProductionCardCollapsedContent = ({ other, cast, crew, tourDates, showEmpty, showText, company, className }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [crewMembers, setCrewMembers] = useState([]);
  const [orchestra, setOrchestra] = useState(null);
  const [chorus, setChorus] = useState(null);

  useEffect(() => {
    setOrchestra(find(crew, item => get(item, 'profession.slug') === 'orchestra'));
    setChorus(find(crew, item => get(item, 'profession.slug') === 'choir'));
    setCrewMembers(filter(crew, item => !['choir', 'orchestra'].includes(get(item, 'profession.slug'))));
  }, [crew]);

  const castGroupByRole = groupBy(
    cast?.map(c => ({ ...c, roleName: c?.workRole?.name || c?.workRole?.original_name })),
    'roleName',
  );
  const otherGroupByRole = groupBy(other, 'workRoleReference');
  const crewGroupByRole = groupBy(
    crewMembers?.map(member => ({ ...member, prof: member?.profession?.name || member?.professionReference })),
    'prof',
  );
  const castData = cast && !!cast.length ? cast : [];
  const companyURL = get(company, 'url');

  return (
    <div className={className}>
      <div className={classes.types}>
        {(crewMembers?.length > 0 || showEmpty) && (
          <div className={classes.teamItem}>
            <TitledBlock title={t(`${TP}.FN_CREW`)} className={classes.teamItemBlock}>
              {isEmpty(crewGroupByRole) ? (
                EMPTY_INFO_MARK
              ) : (
                <CastRow castGroupByRole={crewGroupByRole} showText={showText} />
              )}
            </TitledBlock>
          </div>
        )}
        {(castData?.length > 0 || showEmpty) && (
          <div className={cn(classes.teamItem, classes.castItem)}>
            <TitledBlock title={t(`${TP}.m_CAST`)} className={classes.teamItemBlock}>
              {isEmpty(castGroupByRole) ? (
                EMPTY_INFO_MARK
              ) : (
                <CastRow castGroupByRole={castGroupByRole} showText={showText} />
              )}
            </TitledBlock>
          </div>
        )}
        {(Object.keys(otherGroupByRole).length > 0 || showEmpty) && (
          <div className={cn(classes.teamItem, classes.otherItem)}>
            <TitledBlock title={t(`${TP}.m_OTHER`)} className={classes.teamItemBlock}>
              {isEmpty(otherGroupByRole) ? (
                EMPTY_INFO_MARK
              ) : (
                <CastRow castGroupByRole={otherGroupByRole} showText={showText} />
              )}
            </TitledBlock>
          </div>
        )}
        {tourDates && (tourDates.length > 0 || showEmpty) && (
          <div className={cn(classes.teamItem, classes.tourItem)}>
            <TitledBlock title={t(`${TP}.m_TOURDATES`)}>
              {tourDates.length > 0
                ? tourDates.map(tourDate => (
                    <Grid className={classes.tourRow} item key={`date-${get(tourDate, 'date')}`}>
                      <Grid className={classes.tourDate} item>
                        {get(tourDate, 'date', '')}
                      </Grid>
                      <Grid item>
                        {showText ? (
                          <div className={classes.cellBody}>{get(tourDate, 'venue.name')}</div>
                        ) : (
                          <NextLink
                            href={`${route.VENUES}/[country]/[id]/[[...action]]`}
                            as={getRouteWithSlug(
                              `${route.VENUES}/${get(tourDate, 'venue.country.slug', 'u')}`,
                              get(tourDate, 'venue'),
                            )}
                            target="_blank"
                          >
                            <div className={classes.cellBody}>{get(tourDate, 'venue.name')}</div>
                          </NextLink>
                        )}
                      </Grid>
                    </Grid>
                  ))
                : EMPTY_INFO_MARK}
            </TitledBlock>
          </div>
        )}
      </div>
      {companyURL && (
        <TitledBlock title={t(`${TP}.m_WEBSITE`)} className={classes.teamItemBlock}>
          <OuterLink href={companyURL}>{companyURL}</OuterLink>
        </TitledBlock>
      )}
      {orchestra && (
        <TitledBlock title={t(`${TP}.m_FN_ORCHESTRA`)} className={classes.teamItemBlock}>
          {orchestra.profile ? get(orchestra, 'profile.name') : orchestra.profileReference}
        </TitledBlock>
      )}
      {chorus && (
        <TitledBlock title={t(`${TP}.m_FN_CHORUS`)} className={classes.teamItemBlock}>
          {chorus.profile ? get(chorus, 'profile.name') : chorus.profileReference}
        </TitledBlock>
      )}
    </div>
  );
};

ProductionCardCollapsedContent.propTypes = {
  cast: PropTypes.array,
  crew: PropTypes.array,
  tourDates: PropTypes.array,
  other: PropTypes.array,
  showEmpty: PropTypes.bool,
  showText: PropTypes.bool,
  company: PropTypes.object,
  className: PropTypes.string,
};

ProductionCardCollapsedContent.defaultProps = {
  showEmpty: false,
  showText: false,
  className: '',
};

export default ProductionCardCollapsedContent;
